/*
  Single source of truth for the checklist "definition of done" copy.
  Mirrors Modules/Jobs/Support/ChecklistDoneDefinition.php in the API.

  Each document type (invoice, quotation, report) has a company-wide setting
  (checklist_<type>_done_when) controlling when a task requirement counts as
  satisfied. NOTE: quotation "SENT" means emailed to the client — the
  quotation *status* "SENT" means published and is covered by FINALISED.
*/

export const checklistDoneOptions = {
  invoice: [
    {
      value: 'FINALISED',
      label: 'Finalised (no longer a draft)',
      pendingLabel: 'Invoice required',
      satisfiedLabel: 'Invoice added',
      phrase: 'finalise the invoice',
      explanation: 'The task can be ticked off once an invoice for this appointment is finalised (no longer a draft).'
    },
    {
      value: 'SENT',
      label: 'Sent to the client',
      pendingLabel: 'Invoice not sent',
      satisfiedLabel: 'Invoice sent',
      phrase: 'send the invoice to the client',
      explanation: 'The task can be ticked off once an invoice for this appointment has been emailed to the client.'
    },
    {
      value: 'PAID',
      label: 'Paid in full',
      pendingLabel: 'Awaiting payment',
      satisfiedLabel: 'Invoice paid',
      phrase: 'collect full payment for the invoice',
      explanation: 'The task can be ticked off once an invoice for this appointment is fully paid. Partial payments do not count.'
    }
  ],
  quotation: [
    {
      value: 'FINALISED',
      label: 'Finalised (no longer a draft)',
      pendingLabel: 'Quote required',
      satisfiedLabel: 'Quote added',
      phrase: 'finalise the quote',
      explanation: 'The task can be ticked off once a quote for this appointment is finalised (no longer a draft).'
    },
    {
      value: 'SENT',
      label: 'Sent to the client',
      pendingLabel: 'Quote not sent',
      satisfiedLabel: 'Quote sent',
      phrase: 'send the quote to the client',
      explanation: 'The task can be ticked off once a quote for this appointment has been emailed to the client.'
    },
    {
      value: 'ACCEPTED',
      label: 'Accepted by the client',
      pendingLabel: 'Awaiting acceptance',
      satisfiedLabel: 'Quote accepted',
      phrase: 'have the client accept the quote',
      explanation: 'The task can be ticked off once the client has accepted a quote for this appointment.'
    }
  ],
  report: [
    {
      value: 'PUBLISHED',
      label: 'Published',
      pendingLabel: 'Report required',
      satisfiedLabel: 'Report completed',
      phrase: 'complete the report',
      explanation: 'The task can be ticked off once a report has been published for the linked report template on this appointment.'
    },
    {
      value: 'SENT',
      label: 'Sent to the client',
      pendingLabel: 'Report not sent',
      satisfiedLabel: 'Report sent',
      phrase: 'send the report to the client',
      explanation: 'The task can be ticked off once a published report for the linked report template has been emailed to the client.'
    }
  ]
}

const fallbacks = {
  invoice: 'FINALISED',
  quotation: 'FINALISED',
  report: 'PUBLISHED'
}

// Resolve the option entry for a type + done-when value (with safe fallback)
export function doneOptionFor (type, doneWhen) {
  const options = checklistDoneOptions[type] || []
  return options.find(option => option.value === doneWhen) ||
    options.find(option => option.value === fallbacks[type])
}

// e.g. "send the invoice to the client" — used in blocked-task messaging
export function phraseFor (type, doneWhen) {
  return doneOptionFor(type, doneWhen)?.phrase || ''
}
